import React, { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestSection } from 'components/quests';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { CustomEventNames, logLoyaltyEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { AllowedCelebrationTypes } from 'state/loyalty/hooks/types';
import { useLoyaltyCelebrations } from 'state/loyalty/hooks/use-loyalty-celebrations';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { LoyaltyReward } from 'state/loyalty/types';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/trace';
import { routes } from 'utils/routing';
import { waitForDuration } from 'utils/wait-for-duration';

import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { LoyaltyRewardsExpiringPoints } from './loyalty-rewards-expiring-points';
import { LoyaltyRewardsList } from './loyalty-rewards-list';
import { LoyaltyRewardsSkeletonLoader } from './loyalty-rewards-skeleton-loader';
import { LoyaltyRewardsListContainer } from './loyalty-rewards.styled';
import { useFilteredRewardCategories } from './use-filtered-reward-categories';

/**
 *
 * LoyaltyRewards displays a list of available rewards that can be redeemed at checkout
 *
 */
const LoyaltyRewards: FC = () => {
  const { user, loading: userDataIsLoading, isAuthenticated } = useAuthContext();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const { loading: loyaltyUserLoading, loyaltyUser } = useLoyaltyUser();
  const {
    engineRewardsMap,
    loading: rewardDataIsLoading,
    sanityRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();
  const enableLoyaltyCelebrations = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS);

  const { recheckCelebrations } = useLoyaltyCelebrations({
    allowedCelebrationTypes: [
      AllowedCelebrationTypes.CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.COMBINED_CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED,
    ],
    disableCelebrations: !enableLoyaltyCelebrations,
  });

  const { rewardsEvaluationMap } = useRewardsEvaluation();

  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);

  const router = useRouter();

  const loyaltyId = user?.loyaltyId;

  const rewardCategories = useFilteredRewardCategories();

  // Refetch for updated crowns when navigating to page
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const handleRewardOnPress = useCallback(
    (reward: LoyaltyReward) => {
      startTrace(TraceName.REWARD_DETAIL_LOAD, { id: reward._id, name: reward.name?.locale || '' });
      const rewardId = reward._id;
      logLoyaltyEvent({
        name: CustomEventNames.REWARD_SELECTED,
        incentives: [reward],
      });

      router.navigate(`${routes.loyaltyRewardsList}/${rewardId}`);
    },
    [router]
  );

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    await waitForDuration(1500);
    if (loyaltyId) {
      refetchLoyaltyUser();
      setForceRefresh(true);
      await refetchRewards(loyaltyId);
      setForceRefresh(false);
      recheckCelebrations();
    }
  };

  // We only want to show the skeleton if the user is being fetched when user's data is empty
  const retrievingUser = !loyaltyUser && loyaltyUserLoading;

  // We want to wait for loyalty user data to fully loaded before rendering rewards
  // as some rewards could be marked as unavailable depending on loyalty data
  if (userDataIsLoading || retrievingUser || rewardDataIsLoading || isForceRefreshing) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  if (isAuthenticated && !loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  if (!rewardCategories?.length || !sanityRewardsMap) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      <View style={centeredRowContainerStyles.content}>
        <LoyaltyRewardsExpiringPoints />
        <QuestSection showHeader={false} />
        <LoyaltyRewardsListContainer>
          <LoyaltyRewardsList
            handleOnPress={handleRewardOnPress}
            rewardCategories={rewardCategories}
            engineRewardsMap={engineRewardsMap}
            rewardsEvaluationMap={rewardsEvaluationMap}
            sanityRewardsMap={sanityRewardsMap}
          />
        </LoyaltyRewardsListContainer>
      </View>
    </PullToReloadScrollView>
  );
};

export default LoyaltyRewards;
